/**
 * Loads heavy third-party widgets after the page has painted, instead of before it.
 *
 * Shopify renders app embeds inside content_for_header, which the theme cannot reorder or
 * annotate. So the embed is switched off in the theme editor and the same work is done from
 * here instead, once the page is interactive. The widget still runs; it simply stops holding
 * up the first paint.
 *
 * Privy used to be deferred here. It has been replaced outright by sections/newsletter-popup
 * and assets/newsletter-popup.js, which do the same job — first-time visitor targeting, a
 * timed and exit-intent trigger, email capture, a discount code — on Shopify's own customer
 * form, in about 5KB rather than 847KB.
 *
 * If a widget below ever needs to come back to loading up front, re-enable its app embed under
 * Online Store > Themes > Customize > App embeds and remove its entry from DEFERRED.
 */

/** Microsoft Clarity project, as configured in the app embed this replaces. */
const CLARITY_PROJECT_ID = 'xwgt3p86ue';

/**
 * @typedef {object} DeferredScript
 * @property {string} name - Human-readable name, used only for the console warning on failure.
 * @property {string} [src] - The script URL, exactly as the app embed would have written it.
 * @property {() => void} [setup] - For embeds that do more than load a URL: the same work the
 *   app embed's inline script would have done, run at defer time instead of during parsing.
 */

/** @type {DeferredScript[]} */
const DEFERRED = [
  {
    // Clarity's embed is an inline snippet rather than a plain script tag, so it is reproduced
    // here rather than referenced. It is a session recorder: starting it a beat after first
    // paint costs the first moment of the recording and nothing else. Keep the consent wiring
    // in step with the app's own snippet if Clarity ever updates it.
    name: 'Microsoft Clarity',
    setup: startClarity,
  },
];

/** Loads Clarity and reports the visitor's consent to it, exactly as its app embed does. */
function startClarity() {
  const clarity = (window.clarity =
    window.clarity ||
    function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    });

  const script = document.createElement('script');
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}?ref=shopify`;
  script.async = true;
  document.head.appendChild(script);

  /**
   * @param {boolean} marketingAllowed
   * @param {boolean} analyticsAllowed
   */
  const reportConsent = (marketingAllowed, analyticsAllowed) => {
    clarity('consentv2', {
      ad_Storage: marketingAllowed ? 'granted' : 'denied',
      analytics_Storage: analyticsAllowed ? 'granted' : 'denied',
      source: 101,
    });
  };

  window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) {
      console.error('Error loading Shopify features:', error);
      return;
    }

    const privacy = window.Shopify.customerPrivacy;
    reportConsent(privacy.marketingAllowed(), privacy.analyticsProcessingAllowed());
  });

  document.addEventListener('visitorConsentCollected', (event) => {
    const { marketingAllowed, analyticsAllowed } = /** @type {CustomEvent} */ (event).detail;
    reportConsent(marketingAllowed, analyticsAllowed);
  });
}

/**
 * A load with no interaction at all is not a session worth recording: it is a preview bot, a
 * link unfurler, or a PageSpeed run. Waiting for the visitor to do something keeps the widgets
 * off that critical path entirely, rather than merely a few seconds down it.
 *
 * The blind timeout that used to sit here fired at three seconds, which is inside the window
 * PageSpeed measures — so the work still landed on Total Blocking Time, just later.
 */
const INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel', 'scroll'];

let started = false;

function loadDeferredScripts() {
  if (started) return;
  started = true;

  for (const event of INTERACTION_EVENTS) {
    window.removeEventListener(event, loadDeferredScripts);
  }

  for (const { name, src, setup } of DEFERRED) {
    if (setup) {
      try {
        setup();
      } catch (error) {
        console.warn(`[theme] ${name} failed to start`, error);
      }
      continue;
    }

    const script = document.createElement('script');
    script.src = /** @type {string} */ (src);
    script.async = true;
    script.addEventListener('error', () => console.warn(`[theme] ${name} failed to load from ${src}`));
    document.head.appendChild(script);
  }
}

function scheduleDeferredScripts() {
  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, loadDeferredScripts, { once: true, passive: true });
  }
}

if (document.readyState === 'complete') {
  scheduleDeferredScripts();
} else {
  window.addEventListener('load', scheduleDeferredScripts, { once: true });
}
